/**
 * Humanize a groups RPC error for an admin toast.
 *
 * `supabase.rpc(...)` errors carry the raw Postgres `RAISE EXCEPTION` message as
 * `error.message` — a machine key like `last_organizer` or `group_not_available`
 * (see backend/supabase/migrations/20260930120300_groups_membership_rpcs.sql and
 * 20260930120400_groups_lifecycle_rpcs.sql), never member-facing prose. This maps
 * the keys an ADMIN can actually trigger (role changes, removal, status changes,
 * chapter backfill) to plain text; anything else falls through to the raw
 * message so a truly unexpected error is still visible instead of swallowed.
 * Style mirrors admin/src/lib/fnError.ts (message-key → human text), but this is
 * for `rpc()` calls, not `functions.invoke()`.
 */
const GROUPS_ADMIN_ERROR_COPY: Record<string, string> = {
  group_not_available: "This group isn't available.",
  groups_disabled: 'Groups are turned off — enable them on the Groups Settings page first.',
  not_allowed: "You don't have permission to do that.",
  not_authenticated: 'Your session has expired — sign in again.',
  last_organizer: 'Every group needs an organizer. Promote someone else first.',
  member_not_found: 'That member could not be found in this group.',
  use_leave_group: 'Members leave through their own account, not this screen.',
  use_review_group: "Use the group's Approve/Decline flow while it is pending review.",
  invalid_status: "That status isn't valid here.",
  group_locked: 'This group is suspended or archived and cannot be edited.',
  not_pending: 'This proposal has already been decided.',
  // Groups Phase 4 (pledge board + launch checklist/feature gate).
  idea_not_available: "This idea isn't available (it may already be converted or closed).",
  no_organizer: 'This idea needs a volunteer organizer before it can become a group.',
  below_threshold: "This idea hasn't reached its pledge threshold yet. Use Convert anyway to override.",
  organizer_taken: 'Someone else already volunteered to organize this idea.',
  too_many_ideas: 'That member already has 3 open ideas.',
  launch_incomplete: "Complete the launch checklist before featuring this group, or use Override.",
  // Groups Phase 5 (video meetups + huddles).
  call_not_found: 'That call no longer exists.',
  video_disabled: 'Video meetups are turned off — enable them on the Groups Settings page first.',
  // Groups audience (spec 2026-09-27, migration 20261018120000+): "Who can use
  // Groups" — admins-only preview with named testers.
  classroom_closed: "This Classroom isn't open to members yet — reps can be assigned once it is (Pilot or Everyone).",
  not_in_groups_audience:
    "Only admins and Groups testers can join while Groups isn't open to everyone. Add them as a tester first (Group Settings).",
  chapter_has_memories: "This Classroom has memories and can't be deleted.",
  invalid_state: "That isn't a valid choice.",
}

export function groupsAdminErrorMessage(error: unknown): string {
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error)
  return GROUPS_ADMIN_ERROR_COPY[message] ?? message
}
